import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Film, Search } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.novacast.fibralink.co.in';
const languages = ['Malayalam', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'English'];
const genres = ['Action', 'Comedy', 'Drama', 'Romance', 'Thriller', 'Horror', 'Family', 'Animation', 'Documentary', 'Other'];
const emptyForm = { title: '', description: '', poster_url: '', stream_url: '', language: 'Malayalam', genre: 'Action', year: new Date().getFullYear(), duration_mins: 120, is_premium: true, is_active: true };

export default function Movies() {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchMovies(); }, []);

  const fetchMovies = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/api/movies`, { headers }); setMovies(res.data.data || []); }
    catch { setMovies([]); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.title || !form.stream_url) { setError('Title and Stream URL required'); return; }
    setSaving(true); setError('');
    try {
      if (editId) await axios.put(`${API}/api/movies/${editId}`, form, { headers });
      else await axios.post(`${API}/api/movies`, form, { headers });
      setShowForm(false); setForm(emptyForm); setEditId(null); fetchMovies();
    } catch(e) { setError(e.response?.data?.message || 'Error saving'); }
    setSaving(false);
  };

  const handleEdit = (m) => { setForm({...m}); setEditId(m.id); setShowForm(true); };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this movie?')) return;
    try { await axios.delete(`${API}/api/movies/${id}`, { headers }); fetchMovies(); } catch {}
  };

  const filtered = movies.filter(m => !search || m.title?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{padding:'24px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px'}}>
        <div>
          <h2 style={{margin:0,fontSize:'22px',display:'flex',alignItems:'center',gap:'8px'}}><Film size={22}/> Movies / VOD</h2>
          <p style={{margin:'4px 0 0',color:'var(--text-secondary)',fontSize:'14px'}}>{movies.length} movies · Requires Movies Pack subscription</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}>
          <Plus size={16}/> Add Movie
        </button>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'16px',background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:'8px',padding:'8px 12px',maxWidth:'320px'}}>
        <Search size={14}/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search movies..." style={{background:'none',border:'none',outline:'none',color:'var(--text-primary)',fontSize:'13px',width:'100%'}}/>
      </div>

      {loading ? <div>Loading...</div> : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))',gap:'16px'}}>
          {filtered.map(movie => (
            <div key={movie.id} style={{background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:'12px',overflow:'hidden'}}>
              <div style={{height:'240px',background:'#141420',position:'relative'}}>
                {movie.poster_url
                  ? <img src={movie.poster_url} alt={movie.title} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                  : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}><Film size={48} color="#374151"/></div>
                }
              </div>
              <div style={{padding:'12px'}}>
                <div style={{fontWeight:'700',fontSize:'14px',marginBottom:'4px'}} title={movie.title}>{movie.title.length>22?movie.title.slice(0,22)+'...':movie.title}</div>
                <div style={{fontSize:'11px',color:'var(--text-secondary)',marginBottom:'8px'}}>{movie.language} · {movie.genre}{movie.year?` · ${movie.year}`:''}</div>
                <div style={{display:'flex',gap:'6px'}}>
                  <button className="btn btn-secondary" style={{flex:1,padding:'5px',fontSize:'12px'}} onClick={()=>handleEdit(movie)}><Edit2 size={12}/> Edit</button>
                  <button className="btn btn-danger" style={{padding:'5px 8px',fontSize:'12px'}} onClick={()=>handleDelete(movie.id)}><Trash2 size={12}/></button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length===0 && (
            <div style={{gridColumn:'1/-1',textAlign:'center',padding:'48px',color:'var(--text-secondary)'}}>
              <Film size={48} style={{marginBottom:'12px',opacity:0.3}}/><div>No movies yet. Click "Add Movie" to get started.</div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:'16px',padding:'28px',width:'500px',maxHeight:'90vh',overflowY:'auto'}}>
            <h3 style={{margin:'0 0 20px'}}>{editId?'Edit Movie':'Add Movie'}</h3>
            {error && <div style={{background:'rgba(255,71,87,0.1)',border:'1px solid #FF4757',borderRadius:'8px',padding:'10px',marginBottom:'16px',color:'#FF4757',fontSize:'13px'}}>{error}</div>}
            <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
              <div>
                <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Movie Title *</label>
                <input className="form-control" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Manjummel Boys"/>
              </div>
              <div>
                <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Stream URL * (m3u8, mp4)</label>
                <input className="form-control" value={form.stream_url} onChange={e=>setForm(f=>({...f,stream_url:e.target.value}))} placeholder="https://..."/>
              </div>
              <div>
                <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Poster URL</label>
                <input className="form-control" value={form.poster_url} onChange={e=>setForm(f=>({...f,poster_url:e.target.value}))} placeholder="https://..."/>
              </div>
              <div>
                <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Description</label>
                <textarea className="form-control" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Brief description..." rows={2}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <div>
                  <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Language</label>
                  <select className="form-control" value={form.language} onChange={e=>setForm(f=>({...f,language:e.target.value}))}>{languages.map(l=><option key={l}>{l}</option>)}</select>
                </div>
                <div>
                  <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Genre</label>
                  <select className="form-control" value={form.genre} onChange={e=>setForm(f=>({...f,genre:e.target.value}))}>{genres.map(g=><option key={g}>{g}</option>)}</select>
                </div>
                <div>
                  <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Year</label>
                  <input className="form-control" type="number" value={form.year} onChange={e=>setForm(f=>({...f,year:parseInt(e.target.value)}))}/>
                </div>
                <div>
                  <label style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'6px',display:'block'}}>Duration (mins)</label>
                  <input className="form-control" type="number" value={form.duration_mins} onChange={e=>setForm(f=>({...f,duration_mins:parseInt(e.target.value)}))}/>
                </div>
              </div>
              <div style={{display:'flex',gap:'16px'}}>
                <label style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'14px',cursor:'pointer'}}>
                  <input type="checkbox" checked={form.is_premium} onChange={e=>setForm(f=>({...f,is_premium:e.target.checked}))}/> 🎬 Movies Pack Required
                </label>
                <label style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'14px',cursor:'pointer'}}>
                  <input type="checkbox" checked={form.is_active} onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))}/> Active
                </label>
              </div>
            </div>
            <div style={{display:'flex',gap:'12px',marginTop:'24px'}}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{flex:1}}>{saving?'Saving...':editId?'Update Movie':'Add Movie'}</button>
              <button className="btn btn-secondary" onClick={()=>{setShowForm(false);setError('');}} style={{flex:1}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
